//@flow
import { REHYDRATE } from 'redux-persist';
import { WebNotificationState } from 'common/services/WebNotificationService';
import EventNotificationActions from 'common/redux/actions/eventNotifications';
import { PasswordExpiryNotificationActions } from 'common/redux/actions/password';

import { IntlActions } from 'common/redux/actions/intl';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import Product from 'common/enums/Product';
import { Containers } from 'common/enums/PlanFilterEnum';
import { UPDATE_IS_TOUR_WITH_NO_TASKS_VISIBLE } from 'common/constants/actionTypes';

const DEFAULT_PAGINATION = { page: 1, size: 25 };

const initialState = {
    showV2UI: {
        [Product.MOTION_TRACK]: true,
        [Product.DISPATCHER_PLANNER]: true
    },
    planScreenPagination: {
        [Containers.Planned]: DEFAULT_PAGINATION,
        [Containers.Allocated]: DEFAULT_PAGINATION,
        [Containers.Unallocated]: DEFAULT_PAGINATION
    }
};

const appPreferencesReducer = (
    state: Object = initialState,
    action: Object
): Object => {
    switch (action.type) {
        case REHYDRATE: {
            const savedData =
                action.payload && action.payload.appPreferences
                    ? action.payload.appPreferences
                    : initialState;
            return { ...state, ...savedData };
        }
        case EventNotificationActions.SET_NOTIFICATION_STATE: {
            const { permission } = action;
            const { eventNotifications } = state;
            let notificationState = 'off';
            if (eventNotifications && eventNotifications.notificationState) {
                notificationState =
                    eventNotifications.notificationState || 'off';
            }
            switch (permission) {
                case WebNotificationState.PERMISSION_GRANTED:
                    notificationState = 'on';
                    break;
                case WebNotificationState.PERMISSION_DENIED:
                    notificationState = 'off';
                    break;
                default:
                    notificationState = 'off';
            }
            return {
                ...state,
                eventNotifications: {
                    ...eventNotifications,
                    notificationState
                }
            };
        }
        case EventNotificationActions.SET_PREVIOUS_EVENTS_LIST: {
            const { eventsList } = action;
            const { eventNotifications } = state;
            return {
                ...state,
                eventNotifications: { ...eventNotifications, eventsList }
            };
        }
        case EventNotificationActions.SET_NOTIFICATION_PREVIOUSLY_SHOWN: {
            const { flag: isNotificationsPreviouslyShown } = action;
            const { eventNotifications } = state;
            return {
                ...state,
                eventNotifications: {
                    ...eventNotifications,
                    isNotificationsPreviouslyShown
                }
            };
        }
        case EventNotificationActions.SET_NOTIFICATION_SHOWN_AT: {
            const { shownAt } = action;
            const { eventNotifications } = state;
            return {
                ...state,
                eventNotifications: { ...eventNotifications, shownAt }
            };
        }
        case EventNotificationActions.TOGGLE_NOTIFICATION_INFO: {
            const { flag: notificationInfoActive } = action;
            const { eventNotifications } = state;
            return {
                ...state,
                eventNotifications: {
                    ...eventNotifications,
                    notificationInfoActive
                }
            };
        }

        case UPDATE_IS_TOUR_WITH_NO_TASKS_VISIBLE: {
            const { clientId, personnelId, isVisible } = action.payload;
            if (clientId && personnelId) {
                return {
                    ...state,
                    [clientId]: {
                        ...state[clientId],
                        [personnelId]: {
                            ...(state[clientId]
                                ? state[clientId][personnelId]
                                : {}),
                            isToursWithNoTasksVisible: isVisible
                        }
                    }
                };
            }
            return state;
        }

        case IntlActions.SET_SELECTED_BU: {
            const { clientId, personnelId, selectedBU } = action.payload;
            if (clientId && personnelId) {
                return {
                    ...state,
                    [clientId]: {
                        ...state[clientId],
                        [personnelId]: {
                            ...(state[clientId]
                                ? state[clientId][personnelId]
                                : {}),
                            selectedBU
                        }
                    }
                };
            }
            return state;
        }

        case AppPreferencesActions.SET_TIMEZONE: {
            const { clientId, personnelId, timezone } = action.payload;
            if (clientId) {
                if (personnelId) {
                    return {
                        ...state,
                        [clientId]: {
                            ...state[clientId],
                            [personnelId]: {
                                ...(state[clientId]
                                    ? state[clientId][personnelId]
                                    : {}),
                                timezone
                            }
                        }
                    };
                }
            }
            return state;
        }

        case AppPreferencesActions.SAVE_ENTITY_TABLE_PREFS: {
            const { tableId, preferences } = action.payload;
            return {
                ...state,
                entityTables: {
                    ...state.entityTables,
                    [tableId]: preferences
                }
            };
        }

        case AppPreferencesActions.SHOW_V2_UI: {
            const { enableStatus, product } = action;
            const newV2Status = {
                ...state.showV2UI,
                [product]: enableStatus
            };

            return {
                ...state,
                showV2UI: newV2Status
            };
        }

        case PasswordExpiryNotificationActions.DO_IT_LATER: {
            const { clientId, personnelId, timestamp } = action.payload;
            return {
                ...state,
                [clientId]: {
                    ...state[clientId],
                    [personnelId]: {
                        ...(state[clientId]
                            ? state[clientId][personnelId]
                            : {}),
                        passwordExpiryNotification: {
                            doItLaterAt: timestamp
                        }
                    }
                }
            };
        }

        case AppPreferencesActions.V2_UI_LAST_VIEWED: {
            const { product, date } = action.payload;
            return {
                ...state,
                v2UILastViewed: {
                    ...state.v2UILastViewed,
                    [product]: date
                }
            };
        }

        case AppPreferencesActions.SAVE_PLAN_METRICS_PREFERENCES: {
            const { container, clientId, preferences } = action.payload;
            const { planMetricsPreferences = {} } = state;
            return {
                ...state,
                planMetricsPreferences: {
                    ...planMetricsPreferences,
                    [clientId]: {
                        ...(planMetricsPreferences[clientId] || {}),
                        [container]: preferences
                    }
                }
            };
        }

        case AppPreferencesActions.SET_SHOULD_INCLUDE_INACTIVE_TEAMS: {
            const { isChecked } = action.payload;
            return { ...state, shouldIncludeInactiveTeams: isChecked };
        }

        case AppPreferencesActions.UPDATE_PAGINATION: {
            const { container, page } = action.payload;
            return {
                ...state,
                planScreenPagination: {
                    ...state.planScreenPagination,
                    [container]: page
                }
            };
        }

        case AppPreferencesActions.RESET_PAGE_NUMBER: {
            const { container, pagination } = action.payload;
            const pageInfo = { ...pagination, page: 1 };
            return {
                ...state,
                planScreenPagination: {
                    ...state.planScreenPagination,
                    [container]: pageInfo
                }
            };
        }

        default: {
            return state;
        }
    }
};

export default appPreferencesReducer;
